// ======================== PURCHASES PAGE V3 ========================
// Module professionnel de gestion des achats
// Mobile-first, responsive, avec calculs automatiques

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetFooter,
} from '../components/ui/sheet';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { 
    Plus, Search, ShoppingCart, Trash2, Edit, Check, X, 
    Clock, User, MapPin, Truck, Users, Calendar, AlertCircle,
    CheckCircle, XCircle, FileText, Download, Copy, Archive,
    Send, Package, MoreVertical, Filter, ChevronRight, ChevronDown,
    Wallet, Calculator, History, TrendingUp, AlertTriangle, Link,
    Lightbulb, DollarSign, PackagePlus, RefreshCw
} from 'lucide-react';

// ======================== CONSTANTS ========================

const STATUS_CONFIG = {
    brouillon: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700 border-gray-300', icon: FileText },
    demandee: { label: 'Demandée', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: Send },
    validee: { label: 'Validée', color: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle },
    refusee: { label: 'Refusée', color: 'bg-red-100 text-red-700 border-red-300', icon: XCircle },
    en_cours: { label: 'En cours', color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: ShoppingCart },
    achetee: { label: 'Achetée', color: 'bg-purple-100 text-purple-700 border-purple-300', icon: Package },
    receptionnee: { label: 'Réceptionnée', color: 'bg-teal-100 text-teal-700 border-teal-300', icon: Check },
    archivee: { label: 'Archivée', color: 'bg-slate-100 text-slate-500 border-slate-300', icon: Archive }
};

const PRIORITY_CONFIG = {
    basse: { label: 'Basse', color: 'bg-gray-100 text-gray-600' },
    normale: { label: 'Normale', color: 'bg-blue-100 text-blue-600' },
    haute: { label: 'Haute', color: 'bg-orange-100 text-orange-600' },
    urgente: { label: 'Urgente', color: 'bg-red-100 text-red-600' }
};

const SECTORS = [
    { id: 'cuisine', name: 'Cuisine' },
    { id: 'bar', name: 'Bar' },
    { id: 'salle', name: 'Salle' },
    { id: 'entretien', name: 'Entretien' },
    { id: 'bureau', name: 'Bureau' },
    { id: 'autre', name: 'Autre' }
];

const UNITS = ['unité', 'kg', 'g', 'L', 'mL', 'bouteille', 'carton', 'paquet', 'boîte', 'sac', 'pièce'];

// Format currency to CFA
const formatCFA = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '0 F';
    return new Intl.NumberFormat('fr-FR', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount) + ' F';
};

// ======================== MAIN COMPONENT ========================

const PurchasesPageV3 = () => {
    const { api, user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [lists, setLists] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [shoppers, setShoppers] = useState([]);
    const [stats, setStats] = useState(null);
    const [budgetSummary, setBudgetSummary] = useState(null);
    const [stockSuggestions, setStockSuggestions] = useState([]);
    const [stockProducts, setStockProducts] = useState([]);
    const [priceAlerts, setPriceAlerts] = useState([]);
    
    // UI State
    const [activeTab, setActiveTab] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterSector, setFilterSector] = useState('');
    const [filterPriority, setFilterPriority] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [productSearch, setProductSearch] = useState('');
    
    // Dialogs
    const [createSheetOpen, setCreateSheetOpen] = useState(false);
    const [detailSheetOpen, setDetailSheetOpen] = useState(false);
    const [selectedList, setSelectedList] = useState(null);
    const [validationDialogOpen, setValidationDialogOpen] = useState(false);
    const [suggestionsDialogOpen, setSuggestionsDialogOpen] = useState(false);
    
    // Form State
    const [formData, setFormData] = useState({
        sector: 'cuisine',
        priority: 'normale',
        description: '',
        budget: {
            amount_requested: 0,
            transport: 0,
            labor: 0,
            contingency: 0
        },
        items: []
    });
    
    // Permissions
    const canValidate = ['ADMIN', 'DIRECTEUR'].includes(user?.role);
    const canCreate = true; // À adapter selon vos besoins
    const isAdmin = user?.role === 'ADMIN';

    // ======================== DATA FETCHING ========================

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [listsRes, templatesRes, shoppersRes, statsRes, budgetRes, suggestionsRes] = await Promise.all([
                api().get('/purchase-lists'),
                api().get('/purchase-lists/templates').catch(() => ({ data: [] })),
                api().get('/purchase-lists/shoppers'),
                api().get('/purchase-lists/stats').catch(() => ({ data: null })),
                api().get('/budget/summary?period=month').catch(() => ({ data: null })),
                api().get('/purchase-lists/stock-suggestions').catch(() => ({ data: [] }))
            ]);
            setLists(listsRes.data);
            setTemplates(templatesRes.data);
            setShoppers(shoppersRes.data);
            setStats(statsRes.data);
            setBudgetSummary(budgetRes.data);
            setStockSuggestions(suggestionsRes.data);
        } catch (error) {
            console.error('Fetch error:', error);
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);
    
    // Fetch stock products for linking
    const fetchStockProducts = useCallback(async (search = '') => {
        try {
            const res = await api().get(`/purchase-lists/stock-products?search=${search}&limit=30`);
            setStockProducts(res.data);
        } catch (error) {
            console.error('Stock products fetch error:', error);
        }
    }, [api]);
    
    // Check prices for items
    const checkPrices = useCallback(async (items) => {
        if (!items || items.length === 0) return;
        try {
            const res = await api().post('/purchase-lists/check-prices', items);
            const alerts = res.data.filter(p => p.alert);
            setPriceAlerts(alerts);
        } catch (error) {
            console.error('Price check error:', error);
        }
    }, [api]);

    // ======================== FILTERING ========================

    const filteredLists = useMemo(() => {
        let result = [...lists];
        
        // Filter by tab (status)
        if (activeTab !== 'all') {
            result = result.filter(l => l.status === activeTab);
        }
        
        // Filter by sector
        if (filterSector) {
            result = result.filter(l => l.sector === filterSector);
        }
        
        // Filter by priority
        if (filterPriority) {
            result = result.filter(l => l.priority === filterPriority);
        }
        
        // Search
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(l => 
                l.number?.toLowerCase().includes(term) ||
                l.description?.toLowerCase().includes(term) ||
                l.items?.some(i => i.name?.toLowerCase().includes(term))
            );
        }
        
        return result;
    }, [lists, activeTab, filterSector, filterPriority, searchTerm]);

    // ======================== FORM HANDLERS ========================

    const resetForm = () => {
        setFormData({
            sector: 'cuisine',
            priority: 'normale',
            description: '',
            budget: {
                amount_requested: 0,
                transport: 0,
                labor: 0,
                contingency: 0
            },
            items: []
        });
    };

    const addItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, {
                id: `temp-${Date.now()}`,
                name: '',
                category: '',
                quantity: 1,
                unit: 'unité',
                estimated_unit_price: 0,
                supplier: '',
                purchase_location: '',
                remarks: ''
            }]
        }));
    };

    const updateItem = (index, field, value) => {
        setFormData(prev => {
            const newItems = [...prev.items];
            newItems[index] = { ...newItems[index], [field]: value };
            return { ...prev, items: newItems };
        });
    };

    const removeItem = (index) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    // Calculate totals
    const formTotals = useMemo(() => {
        const itemsTotal = formData.items.reduce((sum, item) => {
            return sum + (parseFloat(item.estimated_unit_price) || 0) * (parseFloat(item.quantity) || 1);
        }, 0);
        
        const transport = parseFloat(formData.budget.transport) || 0;
        const labor = parseFloat(formData.budget.labor) || 0;
        const contingency = parseFloat(formData.budget.contingency) || 0;
        
        return {
            itemsTotal,
            transport,
            labor,
            contingency,
            grandTotal: itemsTotal + transport + labor + contingency
        };
    }, [formData]);

    // ======================== API HANDLERS ========================

    const handleCreate = async (asDraft = true) => {
        if (formData.items.length === 0) {
            toast.error('Ajoutez au moins un article');
            return;
        }
        
        if (formData.items.some(i => !i.name.trim())) {
            toast.error('Tous les articles doivent avoir un nom');
            return;
        }
        
        try {
            const payload = {
                sector: formData.sector,
                priority: formData.priority,
                description: formData.description,
                budget: {
                    amount_requested: parseFloat(formData.budget.amount_requested) || 0,
                    transport: parseFloat(formData.budget.transport) || 0,
                    labor: parseFloat(formData.budget.labor) || 0,
                    contingency: parseFloat(formData.budget.contingency) || 0
                },
                items: formData.items.map(item => ({
                    name: item.name,
                    category: item.category || null,
                    quantity: parseFloat(item.quantity) || 1,
                    unit: item.unit || 'unité',
                    estimated_unit_price: parseFloat(item.estimated_unit_price) || 0,
                    supplier: item.supplier || null,
                    purchase_location: item.purchase_location || null,
                    remarks: item.remarks || null
                }))
            };
            
            console.log('[Purchases] Creating list:', payload);
            
            const response = await api().post('/purchase-lists', payload);
            
            // Si pas brouillon, soumettre directement
            if (!asDraft) {
                await api().post(`/purchase-lists/${response.data.id}/status`, { action: 'submit' });
            }
            
            toast.success(asDraft ? 'Brouillon enregistré' : 'Liste soumise pour validation');
            setCreateSheetOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            console.error('[Purchases] Create error:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        }
    };

    const handleStatusChange = async (listId, action, additionalData = {}) => {
        try {
            await api().post(`/purchase-lists/${listId}/status`, { action, ...additionalData });
            toast.success('Statut mis à jour');
            fetchData();
            setDetailSheetOpen(false);
            setValidationDialogOpen(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du changement de statut');
        }
    };

    const handleDuplicate = async (listId) => {
        try {
            await api().post(`/purchase-lists/${listId}/duplicate`);
            toast.success('Liste dupliquée');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la duplication');
        }
    };

    const handleDelete = async (listId) => {
        if (!confirm('Supprimer ce brouillon ?')) return;
        try {
            await api().delete(`/purchase-lists/${listId}`);
            toast.success('Brouillon supprimé');
            fetchData();
            setDetailSheetOpen(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    const openDetail = (list) => {
        setSelectedList(list);
        setDetailSheetOpen(true);
    };

    // ======================== RENDER HELPERS ========================

    const StatusBadge = ({ status }) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG.brouillon;
        const Icon = config.icon;
        return (
            <Badge variant="outline" className={`${config.color} border gap-1`}>
                <Icon className="h-3 w-3" />
                {config.label}
            </Badge>
        );
    };

    const PriorityBadge = ({ priority }) => {
        const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.normale;
        return (
            <Badge variant="secondary" className={config.color}>
                {config.label}
            </Badge>
        );
    };

    // ======================== LIST CARD (Mobile-friendly) ========================

    const ListCard = ({ list }) => {
        const totals = list.totals || {};
        const hasVariance = totals.variance !== 0 && totals.actual_spent > 0;
        
        return (
            <Card 
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => openDetail(list)}
                data-testid={`purchase-card-${list.id}`}
            >
                <CardContent className="p-4">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono text-sm font-semibold text-primary">
                                    {list.number}
                                </span>
                                <PriorityBadge priority={list.priority} />
                            </div>
                            <StatusBadge status={list.status} />
                        </div>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreVertical className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicate(list.id); }}>
                                    <Copy className="h-4 w-4 mr-2" /> Dupliquer
                                </DropdownMenuItem>
                                {list.status === 'brouillon' && (
                                    <DropdownMenuItem 
                                        onClick={(e) => { e.stopPropagation(); handleDelete(list.id); }}
                                        className="text-red-600"
                                    >
                                        <Trash2 className="h-4 w-4 mr-2" /> Supprimer
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                    
                    {/* Info */}
                    <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <MapPin className="h-3.5 w-3.5" />
                            <span className="capitalize">{list.sector}</span>
                            <span className="text-xs">•</span>
                            <span>{totals.items_count || 0} article(s)</span>
                        </div>
                        
                        {list.description && (
                            <p className="text-muted-foreground line-clamp-2">{list.description}</p>
                        )}
                        
                        {/* Totals */}
                        <div className="flex items-center justify-between pt-2 border-t">
                            <div>
                                <div className="text-xs text-muted-foreground">Estimé</div>
                                <div className="font-semibold">{formatCFA(totals.estimated_total || 0)}</div>
                            </div>
                            {totals.actual_spent > 0 && (
                                <div className="text-right">
                                    <div className="text-xs text-muted-foreground">Dépensé</div>
                                    <div className="font-semibold">{formatCFA(totals.actual_spent)}</div>
                                </div>
                            )}
                            {hasVariance && (
                                <div className="text-right">
                                    <div className="text-xs text-muted-foreground">Écart</div>
                                    <div className={`font-semibold ${totals.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {totals.variance >= 0 ? '+' : ''}{formatCFA(totals.variance)}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Footer */}
                    <div className="flex items-center justify-between mt-3 pt-2 border-t text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {list.created_by_name}
                        </div>
                        <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(list.created_at).toLocaleDateString('fr-FR')}
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    };

    // ======================== STATS BAR ========================

    const StatsBar = () => {
        const statusCounts = useMemo(() => {
            const counts = {};
            lists.forEach(l => {
                counts[l.status] = (counts[l.status] || 0) + 1;
            });
            return counts;
        }, [lists]);

        return (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <Card className="p-3">
                    <div className="text-xs text-muted-foreground">En attente</div>
                    <div className="text-2xl font-bold text-blue-600">
                        {statusCounts.demandee || 0}
                    </div>
                </Card>
                <Card className="p-3">
                    <div className="text-xs text-muted-foreground">En cours</div>
                    <div className="text-2xl font-bold text-yellow-600">
                        {statusCounts.en_cours || 0}
                    </div>
                </Card>
                <Card className="p-3">
                    <div className="text-xs text-muted-foreground">Validées</div>
                    <div className="text-2xl font-bold text-green-600">
                        {statusCounts.validee || 0}
                    </div>
                </Card>
                <Card className="p-3">
                    <div className="text-xs text-muted-foreground">Total</div>
                    <div className="text-2xl font-bold">{lists.length}</div>
                </Card>
            </div>
        );
    };

    // ======================== MAIN RENDER ========================

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="p-4 max-w-7xl mx-auto" data-testid="purchases-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold">Gestion des Achats</h1>
                    <p className="text-muted-foreground text-sm">
                        Créez et suivez vos listes de courses
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {stockSuggestions.length > 0 && (
                        <Button 
                            variant="outline"
                            onClick={() => setSuggestionsDialogOpen(true)}
                            className="relative"
                        >
                            <Lightbulb className="h-4 w-4 mr-2 text-yellow-500" />
                            Suggestions
                            <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                                {stockSuggestions.length}
                            </Badge>
                        </Button>
                    )}
                    <Button 
                        onClick={() => setCreateSheetOpen(true)}
                        className="w-full sm:w-auto"
                        data-testid="new-purchase-btn"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Nouvelle liste
                    </Button>
                </div>
            </div>

            {/* Budget Summary Bar */}
            {budgetSummary && (
                <Card className="mb-4 border-2">
                    <CardContent className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                            <div className="flex items-center gap-3 flex-1">
                                <Wallet className="h-8 w-8 text-primary" />
                                <div className="flex-1">
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="font-medium">Budget {budgetSummary.period_label}</span>
                                        <span className={`font-bold ${budgetSummary.percentage_used >= 100 ? 'text-red-600' : budgetSummary.percentage_used >= 80 ? 'text-orange-600' : 'text-green-600'}`}>
                                            {budgetSummary.percentage_used}% utilisé
                                        </span>
                                    </div>
                                    <Progress 
                                        value={Math.min(budgetSummary.percentage_used, 100)} 
                                        className={`h-3 ${budgetSummary.percentage_used >= 100 ? '[&>div]:bg-red-500' : budgetSummary.percentage_used >= 80 ? '[&>div]:bg-orange-500' : ''}`}
                                    />
                                    <div className="flex justify-between text-xs mt-1 text-muted-foreground">
                                        <span>Dépensé: {formatCFA(budgetSummary.total_spent)}</span>
                                        <span>Reste: {formatCFA(budgetSummary.remaining)}</span>
                                    </div>
                                </div>
                            </div>
                            {budgetSummary.alerts?.length > 0 && (
                                <Badge variant="destructive" className="flex items-center gap-1">
                                    <AlertTriangle className="h-3 w-3" />
                                    {budgetSummary.alerts.length} alerte(s)
                                </Badge>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Stats */}
            <StatsBar />

            {/* Search & Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Rechercher..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                    />
                </div>
                <Button 
                    variant="outline" 
                    onClick={() => setShowFilters(!showFilters)}
                    className="sm:w-auto"
                >
                    <Filter className="h-4 w-4 mr-2" />
                    Filtres
                    {(filterSector || filterPriority) && (
                        <Badge variant="secondary" className="ml-2">
                            {[filterSector, filterPriority].filter(Boolean).length}
                        </Badge>
                    )}
                </Button>
            </div>

            {/* Filter Panel */}
            {showFilters && (
                <Card className="p-4 mb-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <Label className="text-xs">Secteur</Label>
                            <Select value={filterSector} onValueChange={setFilterSector}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Tous secteurs" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="">Tous secteurs</SelectItem>
                                    {SECTORS.map(s => (
                                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Priorité</Label>
                            <Select value={filterPriority} onValueChange={setFilterPriority}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Toutes priorités" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="">Toutes priorités</SelectItem>
                                    {Object.entries(PRIORITY_CONFIG).map(([id, config]) => (
                                        <SelectItem key={id} value={id}>{config.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-end">
                            <Button 
                                variant="ghost" 
                                onClick={() => { setFilterSector(''); setFilterPriority(''); }}
                                className="w-full"
                            >
                                Réinitialiser
                            </Button>
                        </div>
                    </div>
                </Card>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
                <TabsList className="w-full sm:w-auto overflow-x-auto flex-nowrap">
                    <TabsTrigger value="all" className="flex-shrink-0">Toutes</TabsTrigger>
                    <TabsTrigger value="brouillon" className="flex-shrink-0">Brouillons</TabsTrigger>
                    <TabsTrigger value="demandee" className="flex-shrink-0">Demandées</TabsTrigger>
                    <TabsTrigger value="validee" className="flex-shrink-0">Validées</TabsTrigger>
                    <TabsTrigger value="en_cours" className="flex-shrink-0">En cours</TabsTrigger>
                    <TabsTrigger value="receptionnee" className="flex-shrink-0">Reçues</TabsTrigger>
                </TabsList>
            </Tabs>

            {/* Lists */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredLists.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-muted-foreground">
                        <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>Aucune liste trouvée</p>
                        <Button 
                            variant="outline" 
                            className="mt-4"
                            onClick={() => setCreateSheetOpen(true)}
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Créer une liste
                        </Button>
                    </div>
                ) : (
                    filteredLists.map(list => (
                        <ListCard key={list.id} list={list} />
                    ))
                )}
            </div>

            {/* Create Sheet (Mobile-optimized) */}
            <Sheet open={createSheetOpen} onOpenChange={setCreateSheetOpen}>
                <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
                    <SheetHeader>
                        <SheetTitle>Nouvelle liste d'achats</SheetTitle>
                    </SheetHeader>
                    
                    <div className="space-y-6 py-6">
                        {/* General Info */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Secteur</Label>
                                <Select 
                                    value={formData.sector} 
                                    onValueChange={(v) => setFormData(p => ({...p, sector: v}))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SECTORS.map(s => (
                                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Priorité</Label>
                                <Select 
                                    value={formData.priority} 
                                    onValueChange={(v) => setFormData(p => ({...p, priority: v}))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(PRIORITY_CONFIG).map(([id, config]) => (
                                            <SelectItem key={id} value={id}>{config.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        <div>
                            <Label>Description / Motif</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData(p => ({...p, description: e.target.value}))}
                                placeholder="Décrivez le besoin..."
                                rows={2}
                            />
                        </div>
                        
                        <Separator />
                        
                        {/* Budget Block */}
                        <div>
                            <h3 className="font-semibold mb-3 flex items-center gap-2">
                                <Wallet className="h-4 w-4" />
                                Budget
                            </h3>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs">Transport (FCFA)</Label>
                                    <Input
                                        type="number"
                                        value={formData.budget.transport}
                                        onChange={(e) => setFormData(p => ({
                                            ...p, 
                                            budget: {...p.budget, transport: e.target.value}
                                        }))}
                                        placeholder="0"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Main d'œuvre (FCFA)</Label>
                                    <Input
                                        type="number"
                                        value={formData.budget.labor}
                                        onChange={(e) => setFormData(p => ({
                                            ...p, 
                                            budget: {...p.budget, labor: e.target.value}
                                        }))}
                                        placeholder="0"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Imprévus (FCFA)</Label>
                                    <Input
                                        type="number"
                                        value={formData.budget.contingency}
                                        onChange={(e) => setFormData(p => ({
                                            ...p, 
                                            budget: {...p.budget, contingency: e.target.value}
                                        }))}
                                        placeholder="0"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Montant demandé (FCFA)</Label>
                                    <Input
                                        type="number"
                                        value={formData.budget.amount_requested}
                                        onChange={(e) => setFormData(p => ({
                                            ...p, 
                                            budget: {...p.budget, amount_requested: e.target.value}
                                        }))}
                                        placeholder="0"
                                    />
                                </div>
                            </div>
                        </div>
                        
                        <Separator />
                        
                        {/* Items */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-semibold flex items-center gap-2">
                                    <ShoppingCart className="h-4 w-4" />
                                    Articles ({formData.items.length})
                                </h3>
                                <Button size="sm" onClick={addItem} data-testid="add-item-btn">
                                    <Plus className="h-4 w-4 mr-1" />
                                    Article
                                </Button>
                            </div>
                            
                            <div className="space-y-3">
                                {formData.items.map((item, index) => (
                                    <Card key={item.id} className="p-3">
                                        <div className="flex items-start justify-between mb-2">
                                            <span className="text-xs text-muted-foreground">
                                                Article #{index + 1}
                                            </span>
                                            <Button 
                                                variant="ghost" 
                                                size="icon" 
                                                className="h-6 w-6"
                                                onClick={() => removeItem(index)}
                                            >
                                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                                            </Button>
                                        </div>
                                        
                                        <div className="grid grid-cols-1 gap-2">
                                            <Input
                                                placeholder="Nom de l'article *"
                                                value={item.name}
                                                onChange={(e) => updateItem(index, 'name', e.target.value)}
                                                data-testid={`item-name-${index}`}
                                            />
                                            
                                            <div className="grid grid-cols-3 gap-2">
                                                <Input
                                                    type="number"
                                                    placeholder="Qté"
                                                    value={item.quantity}
                                                    onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                                                    min="0.01"
                                                    step="0.01"
                                                />
                                                <Select 
                                                    value={item.unit} 
                                                    onValueChange={(v) => updateItem(index, 'unit', v)}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {UNITS.map(u => (
                                                            <SelectItem key={u} value={u}>{u}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <Input
                                                    type="number"
                                                    placeholder="Prix unit."
                                                    value={item.estimated_unit_price}
                                                    onChange={(e) => updateItem(index, 'estimated_unit_price', e.target.value)}
                                                    min="0"
                                                />
                                            </div>
                                            
                                            <div className="grid grid-cols-2 gap-2">
                                                <Input
                                                    placeholder="Fournisseur"
                                                    value={item.supplier}
                                                    onChange={(e) => updateItem(index, 'supplier', e.target.value)}
                                                />
                                                <Input
                                                    placeholder="Lieu d'achat"
                                                    value={item.purchase_location}
                                                    onChange={(e) => updateItem(index, 'purchase_location', e.target.value)}
                                                />
                                            </div>
                                            
                                            {/* Item Total */}
                                            <div className="text-right text-sm font-medium">
                                                Total: {formatCFA((parseFloat(item.estimated_unit_price) || 0) * (parseFloat(item.quantity) || 1))}
                                            </div>
                                        </div>
                                    </Card>
                                ))}
                                
                                {formData.items.length === 0 && (
                                    <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                                        <ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">Aucun article</p>
                                        <Button 
                                            variant="outline" 
                                            size="sm" 
                                            className="mt-2"
                                            onClick={addItem}
                                        >
                                            <Plus className="h-4 w-4 mr-1" />
                                            Ajouter un article
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {/* Totals Summary */}
                        {formData.items.length > 0 && (
                            <Card className="bg-muted/50 p-4">
                                <h4 className="font-semibold mb-3 flex items-center gap-2">
                                    <Calculator className="h-4 w-4" />
                                    Récapitulatif
                                </h4>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span>Articles ({formData.items.length})</span>
                                        <span>{formatCFA(formTotals.itemsTotal)}</span>
                                    </div>
                                    {formTotals.transport > 0 && (
                                        <div className="flex justify-between text-muted-foreground">
                                            <span>Transport</span>
                                            <span>{formatCFA(formTotals.transport)}</span>
                                        </div>
                                    )}
                                    {formTotals.labor > 0 && (
                                        <div className="flex justify-between text-muted-foreground">
                                            <span>Main d'œuvre</span>
                                            <span>{formatCFA(formTotals.labor)}</span>
                                        </div>
                                    )}
                                    {formTotals.contingency > 0 && (
                                        <div className="flex justify-between text-muted-foreground">
                                            <span>Imprévus</span>
                                            <span>{formatCFA(formTotals.contingency)}</span>
                                        </div>
                                    )}
                                    <Separator />
                                    <div className="flex justify-between font-bold text-lg">
                                        <span>TOTAL</span>
                                        <span className="text-primary">{formatCFA(formTotals.grandTotal)}</span>
                                    </div>
                                </div>
                            </Card>
                        )}
                    </div>
                    
                    <SheetFooter className="flex-col sm:flex-row gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => handleCreate(true)}
                            className="w-full sm:w-auto"
                            disabled={formData.items.length === 0}
                        >
                            <FileText className="h-4 w-4 mr-2" />
                            Enregistrer brouillon
                        </Button>
                        <Button 
                            onClick={() => handleCreate(false)}
                            className="w-full sm:w-auto"
                            disabled={formData.items.length === 0}
                            data-testid="submit-purchase-btn"
                        >
                            <Send className="h-4 w-4 mr-2" />
                            Soumettre pour validation
                        </Button>
                    </SheetFooter>
                </SheetContent>
            </Sheet>

            {/* Detail Sheet */}
            <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
                <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
                    {selectedList && (
                        <>
                            <SheetHeader>
                                <div className="flex items-center gap-3">
                                    <SheetTitle className="font-mono">{selectedList.number}</SheetTitle>
                                    <StatusBadge status={selectedList.status} />
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    {selectedList.description || 'Pas de description'}
                                </p>
                            </SheetHeader>
                            
                            <ScrollArea className="h-[calc(100vh-200px)] py-6">
                                <div className="space-y-6">
                                    {/* Info */}
                                    <Card className="p-4">
                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <span className="text-muted-foreground">Secteur</span>
                                                <p className="font-medium capitalize">{selectedList.sector}</p>
                                            </div>
                                            <div>
                                                <span className="text-muted-foreground">Priorité</span>
                                                <p><PriorityBadge priority={selectedList.priority} /></p>
                                            </div>
                                            <div>
                                                <span className="text-muted-foreground">Créé par</span>
                                                <p className="font-medium">{selectedList.created_by_name}</p>
                                            </div>
                                            <div>
                                                <span className="text-muted-foreground">Date</span>
                                                <p className="font-medium">
                                                    {new Date(selectedList.created_at).toLocaleDateString('fr-FR')}
                                                </p>
                                            </div>
                                        </div>
                                    </Card>
                                    
                                    {/* Budget */}
                                    <Card className="p-4">
                                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                                            <Wallet className="h-4 w-4" />
                                            Budget
                                        </h4>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span>Articles estimés</span>
                                                <span>{formatCFA(selectedList.totals?.estimated_items_total || 0)}</span>
                                            </div>
                                            <div className="flex justify-between text-muted-foreground">
                                                <span>Transport</span>
                                                <span>{formatCFA(selectedList.budget?.transport || 0)}</span>
                                            </div>
                                            <div className="flex justify-between text-muted-foreground">
                                                <span>Main d'œuvre</span>
                                                <span>{formatCFA(selectedList.budget?.labor || 0)}</span>
                                            </div>
                                            <Separator />
                                            <div className="flex justify-between font-bold">
                                                <span>Total prévisionnel</span>
                                                <span>{formatCFA(selectedList.totals?.estimated_total || 0)}</span>
                                            </div>
                                            {selectedList.budget?.validated_amount > 0 && (
                                                <div className="flex justify-between text-green-600">
                                                    <span>Budget validé</span>
                                                    <span>{formatCFA(selectedList.budget.validated_amount)}</span>
                                                </div>
                                            )}
                                            {selectedList.totals?.actual_spent > 0 && (
                                                <>
                                                    <div className="flex justify-between text-purple-600">
                                                        <span>Réellement dépensé</span>
                                                        <span>{formatCFA(selectedList.totals.actual_spent)}</span>
                                                    </div>
                                                    <div className={`flex justify-between font-bold ${selectedList.totals.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                        <span>Écart</span>
                                                        <span>
                                                            {selectedList.totals.variance >= 0 ? '+' : ''}
                                                            {formatCFA(selectedList.totals.variance)}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </Card>
                                    
                                    {/* Items */}
                                    <Card className="p-4">
                                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                                            <ShoppingCart className="h-4 w-4" />
                                            Articles ({selectedList.items?.length || 0})
                                        </h4>
                                        <div className="space-y-2">
                                            {selectedList.items?.map((item, idx) => (
                                                <div key={item.id || idx} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                                                    <div>
                                                        <p className="font-medium">{item.name}</p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {item.quantity} {item.unit}
                                                            {item.supplier && ` • ${item.supplier}`}
                                                        </p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="font-medium">
                                                            {formatCFA(item.estimated_total || (item.estimated_unit_price * item.quantity))}
                                                        </p>
                                                        {item.actual_unit_price && (
                                                            <p className="text-xs text-green-600">
                                                                Réel: {formatCFA(item.actual_total || (item.actual_unit_price * item.quantity))}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </Card>
                                    
                                    {/* Validation Info */}
                                    {selectedList.validation && (
                                        <Card className="p-4">
                                            <h4 className="font-semibold mb-3 flex items-center gap-2">
                                                <History className="h-4 w-4" />
                                                Suivi
                                            </h4>
                                            <div className="space-y-3 text-sm">
                                                {selectedList.validation.prepared_by_name && (
                                                    <div className="flex items-center gap-2">
                                                        <Check className="h-4 w-4 text-green-500" />
                                                        <span>Préparée par {selectedList.validation.prepared_by_name}</span>
                                                    </div>
                                                )}
                                                {selectedList.validation.validated_by_name && (
                                                    <div className="flex items-center gap-2">
                                                        <Check className="h-4 w-4 text-green-500" />
                                                        <span>
                                                            {selectedList.status === 'refusee' ? 'Refusée' : 'Validée'} par {selectedList.validation.validated_by_name}
                                                        </span>
                                                    </div>
                                                )}
                                                {selectedList.validation.purchased_by_name && (
                                                    <div className="flex items-center gap-2">
                                                        <Check className="h-4 w-4 text-green-500" />
                                                        <span>Achetée par {selectedList.validation.purchased_by_name}</span>
                                                    </div>
                                                )}
                                                {selectedList.validation.received_by_name && (
                                                    <div className="flex items-center gap-2">
                                                        <Check className="h-4 w-4 text-green-500" />
                                                        <span>Réceptionnée par {selectedList.validation.received_by_name}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </Card>
                                    )}
                                </div>
                            </ScrollArea>
                            
                            {/* Actions */}
                            <SheetFooter className="flex-wrap gap-2 border-t pt-4">
                                {/* Brouillon: Soumettre */}
                                {selectedList.status === 'brouillon' && (
                                    <>
                                        <Button 
                                            variant="outline"
                                            onClick={() => handleDelete(selectedList.id)}
                                        >
                                            <Trash2 className="h-4 w-4 mr-2" />
                                            Supprimer
                                        </Button>
                                        <Button 
                                            onClick={() => handleStatusChange(selectedList.id, 'submit')}
                                        >
                                            <Send className="h-4 w-4 mr-2" />
                                            Soumettre
                                        </Button>
                                    </>
                                )}
                                
                                {/* Demandée: Valider/Refuser (Direction) */}
                                {selectedList.status === 'demandee' && canValidate && (
                                    <>
                                        <Button 
                                            variant="destructive"
                                            onClick={() => handleStatusChange(selectedList.id, 'reject', { notes: 'Refusé' })}
                                        >
                                            <XCircle className="h-4 w-4 mr-2" />
                                            Refuser
                                        </Button>
                                        <Button 
                                            className="bg-green-600 hover:bg-green-700"
                                            onClick={() => setValidationDialogOpen(true)}
                                        >
                                            <CheckCircle className="h-4 w-4 mr-2" />
                                            Valider
                                        </Button>
                                    </>
                                )}
                                
                                {/* Validée: Démarrer achat */}
                                {selectedList.status === 'validee' && (
                                    <Button 
                                        onClick={() => handleStatusChange(selectedList.id, 'start_purchase')}
                                    >
                                        <ShoppingCart className="h-4 w-4 mr-2" />
                                        Démarrer l'achat
                                    </Button>
                                )}
                                
                                {/* En cours: Marquer achetée */}
                                {selectedList.status === 'en_cours' && (
                                    <Button 
                                        onClick={() => handleStatusChange(selectedList.id, 'mark_purchased')}
                                    >
                                        <Package className="h-4 w-4 mr-2" />
                                        Marquer achetée
                                    </Button>
                                )}
                                
                                {/* Achetée: Réceptionner */}
                                {selectedList.status === 'achetee' && (
                                    <Button 
                                        onClick={() => handleStatusChange(selectedList.id, 'mark_received')}
                                    >
                                        <Check className="h-4 w-4 mr-2" />
                                        Réceptionner
                                    </Button>
                                )}
                                
                                {/* Reçue/Refusée: Archiver */}
                                {['receptionnee', 'refusee'].includes(selectedList.status) && canValidate && (
                                    <Button 
                                        variant="outline"
                                        onClick={() => handleStatusChange(selectedList.id, 'archive')}
                                    >
                                        <Archive className="h-4 w-4 mr-2" />
                                        Archiver
                                    </Button>
                                )}
                                
                                {/* Dupliquer (toujours visible) */}
                                <Button 
                                    variant="outline"
                                    onClick={() => handleDuplicate(selectedList.id)}
                                >
                                    <Copy className="h-4 w-4 mr-2" />
                                    Dupliquer
                                </Button>
                            </SheetFooter>
                        </>
                    )}
                </SheetContent>
            </Sheet>

            {/* Validation Dialog */}
            <Dialog open={validationDialogOpen} onOpenChange={setValidationDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Valider la liste</DialogTitle>
                        <DialogDescription>
                            Confirmez le budget validé pour cette liste d'achats
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        <div>
                            <Label>Budget validé (FCFA)</Label>
                            <Input
                                type="number"
                                placeholder={selectedList?.totals?.estimated_total?.toString() || '0'}
                                id="validated-amount"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Total estimé: {formatCFA(selectedList?.totals?.estimated_total || 0)}
                            </p>
                        </div>
                        <div>
                            <Label>Notes (optionnel)</Label>
                            <Textarea id="validation-notes" placeholder="Commentaires..." />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setValidationDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => {
                                const amount = parseFloat(document.getElementById('validated-amount').value) || selectedList?.totals?.estimated_total;
                                const notes = document.getElementById('validation-notes').value;
                                handleStatusChange(selectedList.id, 'validate', { 
                                    validated_amount: amount,
                                    notes 
                                });
                            }}
                        >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Valider
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PurchasesPageV3;
